import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';
import BaseDialog from '../BaseDialog';
import useApiControl from '../../../common/useApiControl';

const parseChoices = (answers) => {
  if (!answers) return [];
  if (Array.isArray(answers)) return answers;

  try {
    return JSON.parse(answers);
  } catch (error) {
    console.error('Error parsing answers JSON', error);
    return [];
  }
};

const OwnerConfirmRoundResultDialog = ({ canSubmit = true, event, isVisible, onClose, round, onConfirmed, dialogMode, containerRef }) => {
  const [result, setResult] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [message, setMessage] = useState('');
  const choices = useMemo(() => parseChoices(round?.answers), [round?.answers]);
  const selectedLabel = result !== '' ? choices[Number(result)] : '';
  const isManualBetClose = event?.bet_close_mode === 'manual';

  const params = useMemo(() => ({
    eid: round?.event_id,
    result,
  }), [round?.event_id, result]);

  const { executeRequest, loading } = useApiControl({
    action: '/owner_confirm_round_result',
    params,
    method: 'POST',
  });

  useEffect(() => {
    if (!isVisible) return;

    setResult('');
    setConfirming(false);
    setMessage('');
  }, [isVisible, round?.id]);

  const handleBack = () => {
    setConfirming(false);
    setMessage('');
  };

  const handleConfirm = () => {
    if (!canSubmit) return;

    setMessage('');
    executeRequest()
      .then(() => {
        onClose();
        onConfirmed?.();
      })
      .catch((error) => {
        setMessage(error?.response?.data?.message || '結果の確定に失敗しました。');
      });
  };

  return (
    <BaseDialog isVisible={isVisible} onClose={onClose} dialogMode={dialogMode} containerRef={containerRef}>
      <Box sx={{ width: 380, maxWidth: '100%', p: 1 }}>
        <Typography variant="h6" sx={{ fontSize: 17, mb: 0.75 }}>
          結果登録
        </Typography>
        <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1.5 }}>
          結果が決まったら、該当する結果を選択してください。
        </Typography>
        {!canSubmit ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {isManualBetClose
              ? '結果を選択して待機できます。'
              : 'ベット締切処理が完了するまで結果を確定できません。先に結果を選択して待機できます。'}
          </Alert>
        ) : null}

        {message && <Alert severity="warning" sx={{ mb: 1.5 }}>{message}</Alert>}

        {!confirming ? (
          <>
            {round?.type === 0 ? (
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormLabel component="legend">結果を選択してください</FormLabel>
                <RadioGroup value={result} onChange={(event) => setResult(event.target.value)}>
                  {choices.map((choice, index) => (
                    <FormControlLabel
                      key={index}
                      value={String(index)}
                      control={<Radio />}
                      label={choice}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            ) : (
              <Alert severity="info">このラウンド形式の結果登録は未実装です。</Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button variant="outlined" onClick={onClose}>
                閉じる
              </Button>
              <Button
                variant="contained"
                disabled={result === '' || !canSubmit}
                onClick={() => setConfirming(true)}
              >
                {canSubmit ? '選択内容を確認' : '締切後に確認'}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #cbd5e1', mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                登録する結果
              </Typography>
              <Typography sx={{ fontWeight: 900 }}>
                {selectedLabel || '未選択'}
              </Typography>
            </Box>
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              確定後は変更できません。確定後ラウンド結果発表に進みます。
            </Alert>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={handleBack} disabled={loading}>
                選び直す
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleConfirm}
                disabled={loading || result === '' || !canSubmit}
              >
                結果を確定する
              </Button>
            </Box>
          </>
        )}
      </Box>
    </BaseDialog>
  );
};

export default OwnerConfirmRoundResultDialog;
